import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { decidePass, getPass, passAction, updatePass } from '../../api/visitor-passes-client';
import type { PassState, VisitorPassDetail } from '../../interfaces/visitor-pass';
import { PASS_CATEGORY_LABELS } from '../../interfaces/visitor-pass';
import { FormModal } from '../modal/form-modal';
import { ConfirmDialog } from '../modal/confirm-dialog';
import { AppButton } from '../button/app-button';
import { CountedTextarea } from '../counted-textarea/counted-textarea';
import { StatusPill, StatusTone } from '../status-pill/status-pill';
import { FormSkeleton, LoadError } from '../pages/dashboard/dashboard-shared-components/load-state';
import { StateSwitch, Staleable, StatusLabel, listItemMotion, m } from '../../motion';
import toast from '../../utils/toast';
import { describeSchedule, describeVisit, formatPassDate, zoneNote } from '../../utils/pass-schedule';
import { clockDayTime } from '../../function-utils/shift-time';
import { palette, radius, spacing, typography } from '../../style-utils/theme';
import { PassEditorModal, readPassError } from './pass-editor';

/**
 * ONE VISITOR PASS, FOR STAFF (resident-portal Plan 3, RP-3.5).
 *
 * Raised from a visitor record ("View pass"), from a row name on the desk's
 * Expected today screen, and from the manager's Visitor passes register. A
 * modal over the list it was opened from, never a route (owner rule 2026-08-11:
 * details are modals over their lists).
 *
 * Fed by GET /passes/:id: the schedule in one sentence, the host, the next eight
 * visits and the last twenty arrivals — every time in the BUILDING's zone. What
 * the reader may do is the SERVER's answer on the pass (`can_edit`, `can_end`,
 * migration 118's desk setting included), never re-derived from a permission
 * here: a concierge at an "all" building may edit, at "once" may only end a
 * same-day pass of their own, at "off" neither.
 *
 * APPROVAL (migration 125): a pending pass offers Approve / Decline where the
 * server says `can_decide`, and nothing else but Close — it is answered, not
 * paused. A declined pass shows the note the household was sent.
 *
 * LAYERING. It is a FormModal like the record modal it is raised from, and a
 * FormModal like the pass editor it raises: all three sit on the `modal` tier,
 * and each portal is appended when it opens, so the newest is on top. It does
 * not take `modalTop` — that tier is ConfirmDialog's, and a detail on modalTop
 * would paint over the very editor it opens.
 */

const TONES: Record<PassState, StatusTone> = {
  active: 'success',
  paused: 'warning',
  scheduled: 'warning',
  ended: 'neutral',
  pending: 'warning',
};

const stateLabel = (pass: VisitorPassDetail): string => {
  switch (pass.state) {
    case 'active':
      return 'Active';
    case 'pending':
      return 'Waiting for approval';
    case 'paused':
      return pass.paused_until
        ? `Paused until ${formatPassDate(pass.paused_until, { day: 'numeric', month: 'short' })}`
        : 'Paused';
    case 'scheduled':
      return `Starts ${formatPassDate(pass.valid_from, { day: 'numeric', month: 'short' })}`;
    default:
      return 'Ended';
  }
};

const nameOf = (pass: Pick<VisitorPassDetail, 'first_name' | 'last_name'>): string =>
  [pass.first_name, pass.last_name].filter(Boolean).join(' ') || 'Visitor';

const hostOf = (pass: VisitorPassDetail): string =>
  [pass.host?.first_name, pass.host?.last_name].filter(Boolean).join(' ') || 'the resident';

const possessive = (name: string): string => `${name}'s`;

const WINDOW_PILL: Record<string, string> = { early: 'Early', late: 'Late' };

type View = 'loading' | 'error' | 'empty' | 'ready';

export interface PassDetailModalProps {
  /** The pass to show; null while closed. */
  passId: string | null;
  open: boolean;
  onClose: () => void;
  /** The pass was changed or ended from here — the host re-reads its list. */
  onChanged?: () => void;
}

export const PassDetailModal: React.FC<PassDetailModalProps> = ({
  passId,
  open,
  onClose,
  onChanged,
}) => {
  const [detail, setDetail] = useState<VisitorPassDetail | null>(null);
  const [view, setView] = useState<View>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [confirming, setConfirming] = useState<'pause' | 'end' | 'decline' | null>(null);
  const [declineNote, setDeclineNote] = useState('');
  const [approving, setApproving] = useState(false);
  const [resuming, setResuming] = useState(false);
  const seq = useRef(0);
  const inFlight = useRef(false);

  const load = useCallback(
    (quiet = false) => {
      if (!passId) return;
      seq.current += 1;
      const mine = seq.current;
      if (quiet) setRefreshing(true);
      else setView('loading');
      getPass(passId)
        .then(value => {
          if (seq.current !== mine) return;
          setDetail(value);
          setView('ready');
        })
        .catch(error => {
          if (seq.current !== mine) return;
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            setDetail(null);
            setView('empty');
          } else if (!quiet) {
            setView('error');
          } else {
            toast.error('Could not refresh this visitor pass.');
          }
        })
        .finally(() => {
          if (seq.current === mine) setRefreshing(false);
        });
    },
    [passId]
  );

  // A new pass (or a reopen) starts from a skeleton; a closed modal holds
  // nothing, so the next one never flashes the last pass.
  useEffect(() => {
    if (open && passId) {
      setDetail(null);
      load(false);
    }
    if (!open) {
      seq.current += 1;
      setEditorOpen(false);
      setConfirming(null);
    }
  }, [open, passId, load]);

  const changed = () => {
    load(true);
    if (onChanged) onChanged();
  };

  const act = async (action: 'pause' | 'resume' | 'revoke') => {
    if (!detail || inFlight.current) return;
    inFlight.current = true;
    const first = detail.first_name || nameOf(detail);
    try {
      await passAction(detail.pass_id, action);
      toast.success(
        action === 'pause'
          ? `${possessive(first)} pass is paused.`
          : action === 'resume'
          ? `${possessive(first)} pass is active again.`
          : `${possessive(first)} pass has ended.`
      );
    } catch (error) {
      const verb = action === 'revoke' ? 'end' : action;
      const fallback = `Could not ${verb} ${possessive(first)} pass. Try again.`;
      const reading = readPassError(error, {
        limits: detail.limits,
        fallback,
        notFound: 'This pass has already ended.',
      });
      toast.error(reading.status === 400 ? fallback : reading.message);
    } finally {
      inFlight.current = false;
      changed();
    }
  };

  const decide = async (decision: 'approve' | 'reject', note?: string) => {
    if (!detail || inFlight.current) return;
    inFlight.current = true;
    const firstName = detail.first_name || nameOf(detail);
    try {
      await decidePass(detail.pass_id, decision, note);
      toast.success(
        decision === 'approve'
          ? `${possessive(firstName)} pass is approved. The household is told.`
          : `${possessive(firstName)} pass is declined. The household is told.`
      );
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
      toast.error(
        code === 'PASS_DECIDED'
          ? `${possessive(firstName)} pass has already been answered.`
          : `Could not ${decision === 'approve' ? 'approve' : 'decline'} ${possessive(firstName)} pass. Try again.`
      );
    } finally {
      inFlight.current = false;
      changed();
    }
  };

  const name = detail ? nameOf(detail) : '';
  const first = detail ? detail.first_name || name : '';
  const ended = !detail || detail.state === 'ended';
  const pending = detail?.state === 'pending';
  const mayDecide = !!detail && pending && detail.can_decide === true;
  const mayEdit = !!detail && !ended && !pending && detail.can_edit === true;
  const mayEnd = !!detail && !ended && !mayDecide && detail.can_end === true;
  const paused = detail?.state === 'paused';
  const zone = detail?.zone || '';
  const note = zoneNote(zone);

  const footer = (
    <FooterRow>
      {mayDecide ? (
        <>
          <AppButton
            variant="secondary"
            aria-label={`Approve ${possessive(name)} pass`}
            disabled={approving}
            onClick={async () => {
              setApproving(true);
              try {
                await decide('approve');
              } finally {
                setApproving(false);
              }
            }}
          >
            <StatusLabel state={approving ? 'busy' : 'idle'} labels={{ idle: 'Approve', busy: 'Approving…' }} />
          </AppButton>
          <AppButton
            variant="destructive"
            aria-label={`Decline ${possessive(name)} pass`}
            onClick={() => {
              setDeclineNote('');
              setConfirming('decline');
            }}
          >
            Decline
          </AppButton>
        </>
      ) : null}
      {mayEdit ? (
        <AppButton
          variant="secondary"
          aria-label={`Edit ${possessive(name)} pass`}
          onClick={() => {
            setEditorKey(n => n + 1);
            setEditorOpen(true);
          }}
        >
          Edit
        </AppButton>
      ) : null}
      {!mayEdit ? null : paused ? (
        <AppButton
          variant="secondary"
          aria-label={`Resume ${possessive(name)} pass`}
          disabled={resuming}
          onClick={async () => {
            setResuming(true);
            try {
              await act('resume');
            } finally {
              setResuming(false);
            }
          }}
        >
          <StatusLabel state={resuming ? 'busy' : 'idle'} labels={{ idle: 'Resume', busy: 'Resuming…' }} />
        </AppButton>
      ) : (
        <AppButton
          variant="secondary"
          aria-label={`Pause ${possessive(name)} pass`}
          onClick={() => setConfirming('pause')}
        >
          Pause
        </AppButton>
      )}
      {mayEnd ? (
        <AppButton
          variant="destructive"
          aria-label={`End ${possessive(name)} pass`}
          onClick={() => setConfirming('end')}
        >
          End
        </AppButton>
      ) : null}
      <AppButton variant="ghost" onClick={onClose}>
        Close
      </AppButton>
    </FooterRow>
  );

  return (
    <>
      <FormModal
        open={open}
        onClose={onClose}
        title={detail ? name : 'Visitor pass'}
        subtitle={
          detail
            ? `For ${hostOf(detail)}${detail.unit_label ? `, unit ${detail.unit_label}` : ''}`
            : undefined
        }
        footer={footer}
      >
        <StateSwitch state={view} minHeight={320}>
          {view === 'loading' ? (
            <FormSkeleton fields={4} label="the visitor pass" />
          ) : view === 'error' ? (
            <LoadError message="Could not load this visitor pass." onRetry={() => load(false)} />
          ) : view === 'empty' || !detail ? (
            <Plain>This visitor pass is not here any more. It may have been removed.</Plain>
          ) : (
            <Staleable stale={refreshing}>
              <Body>
                <Pills>
                  <StatusPill tone="neutral">{PASS_CATEGORY_LABELS[detail.category] || 'Visitor'}</StatusPill>
                  <StatusPill tone={detail.state ? TONES[detail.state] : 'neutral'} dot>
                    {stateLabel(detail)}
                  </StatusPill>
                </Pills>
                <Facts>
                  <span>{describeSchedule(detail, { today: detail.today })}</span>
                  {detail.company ? <span>{detail.company}</span> : null}
                  {detail.max_party > 1 ? <span>Up to {detail.max_party} people</span> : null}
                  <span>{detail.created_via === 'staff' ? 'Added by the building' : 'Added by a resident'}</span>
                  {detail.note ? <Muted>Note for the front desk: {detail.note}</Muted> : null}
                  {detail.decision === 'rejected' ? (
                    <Muted>
                      Declined by the building
                      {detail.decision_note ? `: ${detail.decision_note}` : '.'}
                    </Muted>
                  ) : null}
                </Facts>

                <SectionTitle>Next visits</SectionTitle>
                {detail.occurrences.length ? (
                  <List>
                    {detail.occurrences.map((occurrence, index) => (
                      <Item key={`${occurrence.local_date}-${occurrence.starts_at}`} {...listItemMotion(index)} layout={false}>
                        {describeVisit(occurrence, zone, detail.today)}
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Muted>
                    {detail.state === 'ended'
                      ? 'This pass has ended.'
                      : pending
                      ? 'No visits until the pass is approved.'
                      : paused
                      ? 'No visits while paused.'
                      : 'No more visits on this pass.'}
                  </Muted>
                )}

                <SectionTitle>Recent arrivals</SectionTitle>
                {detail.arrivals.length ? (
                  <List>
                    {detail.arrivals.map((arrival, index) => (
                      <Item key={arrival.id} {...listItemMotion(index)} layout={false}>
                        <span>
                          {clockDayTime(arrival.arrived_at, zone)}
                          {arrival.departed_at
                            ? ` · left ${clockDayTime(arrival.departed_at, zone)}`
                            : ' · still inside'}
                          {arrival.party > 1 ? ` · party of ${arrival.party}` : ''}
                        </span>
                        {arrival.window_state && WINDOW_PILL[arrival.window_state] ? (
                          <StatusPill tone="warning">{WINDOW_PILL[arrival.window_state]}</StatusPill>
                        ) : null}
                      </Item>
                    ))}
                  </List>
                ) : (
                  <Muted>{first ? `${first} has not been signed in on this pass yet.` : 'Nobody has been signed in on this pass yet.'}</Muted>
                )}
                {note ? <Muted>{note}</Muted> : null}
              </Body>
            </Staleable>
          )}
        </StateSwitch>
      </FormModal>

      {detail && mayEdit ? (
        <PassEditorModal
          key={editorKey}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          mode="staff"
          host={{
            occupancy_id: detail.host_occupancy_id,
            name: hostOf(detail),
            unit_label: detail.unit_label,
          }}
          limits={detail.limits}
          today={detail.today}
          zone={detail.zone}
          initial={detail}
          notice="The household will be told."
          onSubmit={spec => updatePass(detail.pass_id, spec)}
          onSaved={() => {
            setEditorOpen(false);
            changed();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={open && confirming === 'pause'}
        title={`Pause ${possessive(first)} pass?`}
        message={`The front desk won't see ${first} on the expected list until the pass is resumed.`}
        primaryLabel="Pause pass"
        dismissLabel="Never mind"
        onPrimary={async () => {
          await act('pause');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={open && confirming === 'decline'}
        title={`Decline ${possessive(first)} pass?`}
        message={`The front desk won't expect ${first}. The household is told, and reads your note on the pass.`}
        primaryLabel="Decline pass"
        primaryVariant="destructive"
        dismissLabel="Keep waiting"
        onPrimary={async () => {
          await decide('reject', declineNote);
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      >
        <CountedTextarea
          id="decline-note-detail"
          label="Note to the household (optional)"
          value={declineNote}
          max={detail?.limits?.note_max ?? 280}
          rows={3}
          onChange={event => setDeclineNote(event.target.value)}
        />
      </ConfirmDialog>
      <ConfirmDialog
        open={open && confirming === 'end'}
        title={`End ${possessive(first)} pass?`}
        message={`The front desk will stop expecting ${first}. An ended pass can't be restarted; add a new one if they need it again.`}
        primaryLabel="End pass"
        primaryVariant="destructive"
        dismissLabel="Keep pass"
        onPrimary={async () => {
          await act('revoke');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
    </>
  );
};

/* ──────────────────────────────  Styles  ──────────────────────────────── */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

const Facts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
`;

const SectionTitle = styled.h3`
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

/* A plain block stack, not a zero-gap flex column: the rows are ruled apart
   by their own hairline, and the scanner reads gap 0 on a flex box as rows
   touching (gap-zero). */
const List = styled.ul`
  display: block;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  overflow: hidden;
`;

const Item = m.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.xs} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};

  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

const Muted = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Plain = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
`;

const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};
  width: 100%;
`;
